import { Tooltip } from "@mui/material";
import { TabelaVirtualizadaGenerica } from "components/TabelaVirtualizadaGenerica/TabelaVirtualizadaGenerica";
import { colunasEmpresaInicial } from "contexts/ContextConstants";
import { useProfile } from "contexts/ProfileContext";
import { useToast } from "contexts/ToastContext";
import IconButton from "core/components/IconButton";
import MotionContainer from "core/components/MotionContainer";
import MotionSemAutorizacao from "core/components/MotionSemAutorizacao";
import Separator from "core/components/Separator";
import SelectInputField from "core/data-entry/SelectInputField";
import Layout from "core/layout/Layout";
import {
	anoAtual,
	dateNow,
	memoizedMonthsOptions,
	mesAtual,
	MONTHS_PT,
} from "core/utils/UtilityDates";
import { motion } from "framer-motion";
import useService from "hooks/useService";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	buscarMetaFilial,
	MetaFilial,
} from "services/ModuloGerenciador/MetaFilial";
import "./MetaFilial.css";

const contextosParaRemover = [
	"gloriaDeDourados2",
	"gloriaDeDourados3",
	"templateEmbarque",
];

type MetaFilialParams = {
	mes: string;
	ano: string;
	filial: string;
};

function MetaFilialHeader({
	params,
	setParams,
	onBuscar,
	loading,
	onClear,
}: {
	params: MetaFilialParams;
	setParams: (patch: Partial<MetaFilialParams>) => void;
	onBuscar: () => void;
	loading?: boolean;
	onClear: () => void;
}) {
	const { dadosFilial } = useProfile();

	const years = useMemo(() => {
		const currentYear = dateNow.getFullYear();
		return Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
	}, []);

	const memoizedFilialOptions = useMemo(() => {
		return (
			dadosFilial
				?.filter((f) => !contextosParaRemover.includes(f.contexto))
				.map((f) => ({
					obj: f || "",
					title: f.nomeFilial || "",
				})) || []
		);
	}, [dadosFilial]);

	return (
		<div className="meta-filial-header-container">
			<div className="meta-filial-filtros-container">
				<div className="meta-filial-select-input">
					<SelectInputField
						label="Mês"
						disabled={loading}
						value={
							memoizedMonthsOptions().find((m) => m.obj === params.mes)
								?.title || ""
						}
						onChange={(obj) => {
							setParams({ mes: obj.obj });
						}}
						options={memoizedMonthsOptions()}
					/>
				</div>
				<div className="meta-filial-select-input">
					<SelectInputField
						label="Ano"
						disabled={loading}
						value={params.ano}
						onChange={(obj) => setParams({ ano: obj.obj })}
						options={years.map((y) => ({
							obj: y.toString(),
							title: y.toString(),
						}))}
					/>
				</div>

				<div className="meta-filial-select-input">
					<SelectInputField
						label="Filial"
						disabled={loading}
						value={
							memoizedFilialOptions.find(
								(f) => f.obj.contexto === params.filial,
							)?.title || "Todos"
						}
						onChange={(obj) => setParams({ filial: obj.obj.contexto })}
						options={memoizedFilialOptions}
					/>
				</div>
			</div>

			<motion.div
				transition={{ duration: 0.3 }}
				className="meta-filial-botoes-container"
			>
				<motion.div
					initial={{ rotate: 0 }}
					whileHover={{ rotate: 90 }}
					transition={{ duration: 0.2, ease: "easeInOut" }}
					className="select-field-chevron"
				>
					<Tooltip title="Pesquisar">
						<IconButton
							icon="Search"
							theme="dark"
							size="xl"
							borderColor="none"
							backgroundColor="#FFFFFF"
							hoverColor="#CCC"
							onClick={onBuscar}
							disabled={loading}
						/>
					</Tooltip>
				</motion.div>
				<motion.div
					initial={{ rotate: 0 }}
					whileHover={{ rotate: 90 }}
					transition={{ duration: 0.2, ease: "easeInOut" }}
					className="select-field-chevron"
				>
					<Tooltip title="Limpar Filtros">
						<IconButton
							icon="X"
							size="xl"
							borderColor="none"
							onClick={onClear}
							disabled={loading}
							backgroundColor="rgba(0, 0, 0, 0)"
							theme="dark"
						/>
					</Tooltip>
				</motion.div>
			</motion.div>
		</div>
	);
}

function MetasFilial() {
	const { profile, hasRegra } = useProfile();
	const { showToast } = useToast();

	const [dadosMetaFilial, setDadosMetaFilial] = useState<MetaFilial[]>([]);
	const [semAutorizacao, setSemAutorizacao] = useState(false);
	const [params, setParamsState] = useState<MetaFilialParams>(() => ({
		mes: mesAtual,
		ano: anoAtual,
		filial: "todos",
	}));

	const hasBuscadoMetaFilial = useRef(false);
	const lastSearchedRef = useRef<MetaFilialParams>(params);

	const [buscandoMetaFilial, buscarMeta] = useService(buscarMetaFilial, {
		autoStart: false,
		onData: (dados: MetaFilial[]) => {
			setDadosMetaFilial(dados || []);
		},
		onError: (error) => {
			if (error?.response?.status === 404) {
				showToast("Nenhum dado encontrado para os filtros informados", "info");
				return;
			}
			showToast("Erro ao buscar Metas da Filial", "error");
		},
	});

	const setParams = useCallback((patch: Partial<MetaFilialParams>) => {
		setParamsState((prev) => ({ ...prev, ...patch }));
	}, []);

	const handleBuscarMeta = useCallback(() => {
		if (!profile?.token) return;
		lastSearchedRef.current = params;
		buscarMeta({
			token: profile.token,
			mes: params.mes,
			ano: params.ano,
			filial: params.filial,
		});
	}, [profile?.token, buscarMeta, params]);

	const buscarMetaInicial = useCallback(() => {
		if (profile?.token) {
			const initial = { mes: mesAtual, ano: anoAtual, filial: "todos" };
			lastSearchedRef.current = initial;
			buscarMeta({
				token: profile.token,
				mes: mesAtual,
				ano: anoAtual,
				filial: "todos",
			});
		}
	}, [buscarMeta, profile?.token]);

	const handleClear = useCallback(() => {
		setParams({
			mes: mesAtual,
			ano: anoAtual,
			filial: "todos",
		});
		buscarMetaInicial();
	}, [setParams, buscarMetaInicial]);

	useEffect(() => {
		if (profile?.token && !hasBuscadoMetaFilial.current) {
			buscarMetaInicial();
			setSemAutorizacao(!hasRegra("Meta Filial"));
			hasBuscadoMetaFilial.current = true;
		}
	}, [profile?.token, buscarMetaInicial, hasRegra]);

	const isLoading = useMemo(() => {
		return (
			buscandoMetaFilial || (!profile?.token && !hasBuscadoMetaFilial.current)
		);
	}, [buscandoMetaFilial, profile?.token]);

	if (semAutorizacao) {
		return <MotionSemAutorizacao />;
	}

	return (
		<Layout title="Meta Filial">
			<MotionContainer animationKey="MetaFilialScreen">
				<Separator size="xl" />
				<MetaFilialHeader
					params={params}
					setParams={setParams}
					onBuscar={handleBuscarMeta}
					loading={isLoading}
					onClear={handleClear}
				/>
				<Separator size="2xl" />
				<div className="meta-filial-titulo-container">
					<span>
						{`Meta Filial - ${
							MONTHS_PT[parseInt(lastSearchedRef.current.mes, 10) - 1]
								.charAt(0)
								.toUpperCase() +
							MONTHS_PT[parseInt(lastSearchedRef.current.mes, 10) - 1].slice(1)
						} / ${lastSearchedRef.current.ano}`}
					</span>
				</div>
				<Separator size="xl" />
				<TabelaVirtualizadaGenerica
					colunas={colunasEmpresaInicial.MetaFilial}
					dados={dadosMetaFilial}
					rowHeight={20}
					isLoading={isLoading}
				/>
			</MotionContainer>
		</Layout>
	);
}

export default MetasFilial;
